// ===============================================
// File: src/components/ContactWidget.jsx
// ===============================================
import React, { useMemo, useState } from "react";
import { Phone, Mail, MessageCircle, X, Copy, Check } from "lucide-react";

const ContactWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [copiedKey, setCopiedKey] = useState("");

  // --- General Support ---
  const GEN_EMAIL = "support@yourspotrented.com";
  const GEN_PHONE_DISPLAY = "‪(331) 248-5707";
  const GEN_PHONE_TEL = "+13312485707";

  // --- Blackout Support ---
  const BO_EMAIL = "customer.support@yourspotrented.com";
  const BO_PHONE_DISPLAY = "‪(708) 475-0760‬";
  const BO_PHONE_TEL = "+17084750760";

  const userEmail = useMemo(() => {
    try {
      return (
        localStorage.getItem("user_email") ||
        localStorage.getItem("admin_last_email") ||
        sessionStorage.getItem("user_email") ||
        ""
      );
    } catch {
      return "";
    }
  }, []);

  const subject = "Support Request";
  const boSubject = "Blackout Support Request";

  const baseBody = (hint) =>
    [
      `Hi ${hint ? hint : "Support"},`,
      "",
      "I need help with:",
      "• (briefly describe your issue here)",
      "",
      `My email: ${userEmail || "<your email>"}`,
      "",
      "Thanks,",
    ].join("\n");

  const mailto = (to, sub, body) =>
    `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(
      sub
    )}&body=${encodeURIComponent(body)}`;

  const gmail = (to, sub, body) =>
    `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(
      to
    )}&su=${encodeURIComponent(sub)}&body=${encodeURIComponent(body)}`;

  const genMailtoHref = mailto(GEN_EMAIL, subject, baseBody("Support"));
  const genGmailHref = gmail(GEN_EMAIL, subject, baseBody("Support"));
  const boMailtoHref = mailto(
    BO_EMAIL,
    boSubject,
    baseBody("Blackout Support")
  );
  const boGmailHref = gmail(BO_EMAIL, boSubject, baseBody("Blackout Support"));

  const copy = async (key, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(""), 1200);
    } catch {}
  };

  return (
    <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end">
      {/* Toggle Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-4 rounded-full shadow-lg hover:scale-110 active:scale-95 transition-transform duration-200"
          aria-label="Contact Support"
        >
          <MessageCircle size={24} />
        </button>
      )}

      {/* Expanded */}
      {isOpen && (
        <div className="bg-white rounded-2xl shadow-2xl w-80 max-w-[92vw] p-4 mt-2 border border-blue-100 backdrop-blur-md">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-gray-900 font-semibold text-base">
              Contact Support
            </h3>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>

          {/* Blackout Support */}
          <div className="mb-3 rounded-xl border border-blue-200/60 bg-blue-50/60 p-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">
                Blackout Support
              </span>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-600 text-white">
                Priority
              </span>
            </div>

            {/* Phone */}
            <a
              href={`tel:${BO_PHONE_TEL}`}
              className="mt-2 flex items-center gap-3 p-2 rounded-lg hover:bg-white transition-colors"
            >
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 text-blue-700">
                <Phone size={16} />
              </div>
              <span className="text-sm text-gray-800 font-medium">
                {BO_PHONE_DISPLAY}
              </span>
            </a>

            {/* Email (mobile) */}
            <div className="mt-1 flex items-center gap-2">
              <a
                href={boMailtoHref}
                className="flex md:hidden items-center gap-2 p-2 rounded-lg hover:bg-white transition-colors flex-1"
                title="Open your email app"
              >
                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 text-blue-700">
                  <Mail size={16} />
                </div>
                <span className="text-sm text-gray-800 font-medium">
                  Email via your app
                </span>
              </a>
              <a
                href={boGmailHref}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden md:flex items-center gap-2 p-2 rounded-lg hover:bg-white transition-colors flex-1"
                title="Open Gmail compose"
              >
                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 text-blue-700">
                  <Mail size={16} />
                </div>
                <span className="text-sm text-gray-800 font-medium">
                  Email via Gmail
                </span>
              </a>
              <button
                onClick={() => copy("bo", BO_EMAIL)}
                className="p-2 rounded-lg hover:bg-white transition-colors"
                aria-label="Copy blackout support email"
                title="Copy email"
              >
                {copiedKey === "bo" ? (
                  <Check size={16} className="text-green-600" />
                ) : (
                  <Copy size={16} className="text-gray-500" />
                )}
              </button>
            </div>
          </div>

          {/* General Support */}
          <div className="rounded-xl border border-blue-100 p-3">
            <span className="text-xs font-semibold text-gray-800 uppercase tracking-wide">
              General Support
            </span>

            {/* Phone */}
            <a
              href={`tel:${GEN_PHONE_TEL}`}
              className="mt-2 flex items-center gap-3 p-2 rounded-lg hover:bg-blue-50 transition-colors"
            >
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-green-100 text-green-700">
                <Phone size={16} />
              </div>
              <span className="text-sm text-gray-800 font-medium">
                {GEN_PHONE_DISPLAY}
              </span>
            </a>

            {/* Email */}
            <div className="mt-1 flex items-center gap-2">
              <a
                href={genMailtoHref}
                className="flex md:hidden items-center gap-2 p-2 rounded-lg hover:bg-blue-50 transition-colors flex-1"
                title="Open your email app"
              >
                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 text-blue-700">
                  <Mail size={16} />
                </div>
                <span className="text-sm text-gray-800 font-medium">
                  Email via your app
                </span>
              </a>
              <a
                href={genGmailHref}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden md:flex items-center gap-2 p-2 rounded-lg hover:bg-blue-50 transition-colors flex-1"
                title="Open Gmail compose"
              >
                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 text-blue-700">
                  <Mail size={16} />
                </div>
                <span className="text-sm text-gray-800 font-medium">
                  Email via Gmail
                </span>
              </a>
              <button
                onClick={() => copy("gen", GEN_EMAIL)}
                className="p-2 rounded-lg hover:bg-blue-50 transition-colors"
                aria-label="Copy support email"
                title="Copy email"
              >
                {copiedKey === "gen" ? (
                  <Check size={16} className="text-green-600" />
                ) : (
                  <Copy size={16} className="text-gray-500" />
                )}
              </button>
            </div>
          </div>

          {/* Hint (mobile) */}
          <p className="mt-3 text-[11px] text-gray-500 md:hidden">
            Tip: “Email via your app” uses your default email account.
          </p>
        </div>
      )}
    </div>
  );
};

export default ContactWidget;
